import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "./icons";
import { supabase } from "../lib/supabase";
import { CAPSULE_LOGO_URL } from "../lib/brand";
import { ThemeToggle } from "./theme-toggle";
import { AccessProvider, type AdminPermission, useAccess } from "../lib/access";
import "../workspace-nav.css";

type Can = (permission: AdminPermission) => boolean;
type NavigationItem = {
  to: string;
  label: string;
  icon: Parameters<typeof Icon>[0]["name"];
  matches: string[];
};
type WorkspaceItem = {
  to: string;
  label: string;
  permission?: AdminPermission;
};

const matchesPath = (pathname: string, paths: string[]) =>
  paths.some((path) =>
    path === "/" ? pathname === "/" : pathname === path || pathname.startsWith(`${path}/`),
  );

function primaryNavigation(can: Can) {
  const sections: Array<{ label: string; items: NavigationItem[] }> = [
    {
      label: "Operate",
      items: [
        { to: "/", label: "Overview", icon: "dashboard", matches: ["/"] },
        ...(can("orders.manage")
          ? [
              {
                to: "/orders",
                label: "Sales",
                icon: "orders" as const,
                matches: ["/orders", "/reconciliation"],
              },
            ]
          : []),
        {
          to: "/products",
          label: "Inventory",
          icon: "box",
          matches: ["/products", "/categories", "/alerts", "/purchasing"],
        },
      ],
    },
    {
      label: "Manage",
      items: [
        ...(can("customers.manage") ||
        can("resellers.manage") ||
        can("subscribers.view")
          ? [
              {
                to: can("customers.manage")
                  ? "/customers"
                  : can("resellers.manage")
                    ? "/resellers"
                    : "/subscribers",
                label: "Customers",
                icon: "users" as const,
                matches: ["/customers", "/resellers", "/subscribers", "/customer-care"],
              },
            ]
          : []),
        ...(can("analytics.view") || can("finance.manage")
          ? [
              {
                to: can("analytics.view") ? "/analytics" : "/finance",
                label: "Insights",
                icon: "chart" as const,
                matches: ["/analytics", "/finance"],
              },
            ]
          : []),
        ...(can("broadcasts.manage")
          ? [
              {
                to: "/settings",
                label: "Broadcasts",
                icon: "bell" as const,
                matches: ["/settings"],
              },
            ]
          : []),
      ],
    },
    {
      label: "Control",
      items:
        can("audit.view") || can("staff.manage")
          ? [
              {
                to: can("audit.view") ? "/logs" : "/staff",
                label: "Administration",
                icon: "shield",
                matches: ["/logs", "/staff"],
              },
            ]
          : [],
    },
  ];
  return sections.filter((section) => section.items.length);
}

function workspaceFor(pathname: string): {
  label: string;
  items: WorkspaceItem[];
} | null {
  if (matchesPath(pathname, ["/orders", "/reconciliation"]))
    return {
      label: "Sales workspace",
      items: [
        { to: "/orders", label: "Orders", permission: "orders.manage" },
        {
          to: "/orders/reconciliation",
          label: "Reconcile sales",
          permission: "orders.manage",
        },
      ],
    };
  if (
    matchesPath(pathname, ["/products", "/categories", "/alerts", "/purchasing"])
  )
    return {
      label: "Inventory workspace",
      items: [
        { to: "/products", label: "Products" },
        { to: "/products/categories", label: "Categories" },
        {
          to: "/alerts",
          label: "Stock alerts",
          permission: "alerts.manage",
        },
        {
          to: "/purchasing",
          label: "Purchasing",
          permission: "purchasing.manage",
        },
      ],
    };
  if (matchesPath(pathname, ["/customers", "/resellers", "/subscribers", "/customer-care"]))
    return {
      label: "Customer workspace",
      items: [
        {
          to: "/customers",
          label: "Customers",
          permission: "customers.manage",
        },
        {
          to: "/resellers",
          label: "Resellers",
          permission: "resellers.manage",
        },
        {
          to: "/subscribers",
          label: "Subscribers",
          permission: "subscribers.view",
        },
        {
          to: "/customer-care",
          label: "Support & reviews",
          permission: "customers.manage",
        },
      ],
    };
  if (matchesPath(pathname, ["/analytics", "/finance"]))
    return {
      label: "Insights workspace",
      items: [
        {
          to: "/analytics",
          label: "Analytics",
          permission: "analytics.view",
        },
        {
          to: "/finance",
          label: "Finance & controls",
          permission: "finance.manage",
        },
      ],
    };
  if (matchesPath(pathname, ["/logs", "/staff"]))
    return {
      label: "Administration workspace",
      items: [
        { to: "/logs", label: "Audit log", permission: "audit.view" },
        { to: "/staff", label: "Staff access", permission: "staff.manage" },
      ],
    };
  return null;
}

export function Shell({ children }: { children: ReactNode }) {
  return (
    <AccessProvider>
      <ShellContent>{children}</ShellContent>
    </AccessProvider>
  );
}

function ShellContent({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { access, can } = useAccess();
  const workspace = workspaceFor(pathname);
  const workspaceItems =
    workspace?.items.filter(
      (item) => !item.permission || can(item.permission),
    ) ?? [];
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">
            <img src={CAPSULE_LOGO_URL} alt="" />
          </span>
          <div>
            <strong>Capsule Corp.</strong>
            <small>Stock control</small>
          </div>
          <ThemeToggle className="mobile-theme-toggle" />
          <button
            className="mobile-signout"
            onClick={() => void supabase.auth.signOut()}
          >
            Sign out
          </button>
        </div>
        <nav aria-label="Primary navigation">
          {primaryNavigation(can).map((section) => (
            <section className="nav-section" key={section.label}>
              <span>{section.label}</span>
              {section.items.map((item) => {
                const active = matchesPath(pathname, item.matches);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={active ? "active" : ""}
                    aria-current={active ? "page" : undefined}
                  >
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </section>
          ))}
        </nav>
        <div className="sidebar-foot">
          <span className="account-summary">
            {access ? `${access.role} · ${access.email}` : "Checking access…"}
          </span>
          <ThemeToggle />
          <button
            className="text-button"
            onClick={() => void supabase.auth.signOut()}
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="main">
        <div className="topline">
          <span>
            <i />
            Capsule Corp. inventory network
          </span>
          <span>
            {new Intl.DateTimeFormat("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }).format(new Date())}
          </span>
        </div>
        {workspace && workspaceItems.length > 1 ? (
          <nav className="workspace-nav" aria-label={workspace.label}>
            {workspaceItems.map((item) => {
              const active =
                pathname === item.to ||
                (item.to !== "/products" &&
                  item.to !== "/orders" &&
                  pathname.startsWith(`${item.to}/`)) ||
                (item.to === "/products" &&
                  matchesPath(pathname, ["/products"]) &&
                  pathname !== "/products/categories") ||
                (item.to === "/orders" &&
                  matchesPath(pathname, ["/orders"]) &&
                  pathname !== "/orders/reconciliation");
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={active ? "active" : ""}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        ) : null}
        {children}
      </main>
    </div>
  );
}
